import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  TrendUp, TrendDown, Trophy, Robot, Coins, DeviceMobile,
  CaretLeft, CaretRight, Lightning, Crown, ChartLineUp,
} from '@phosphor-icons/react';

/* ========================================================================== *
 *  DIVE_CONFIG — every "feel" value lives here. Tune without touching layout.
 * ========================================================================== */
export const DIVE_CONFIG = {
  perspective: 1400,
  spring:     { type: 'spring', stiffness: 200, damping: 23, mass: 0.85 },
  dragSpring: { type: 'spring', stiffness: 380, damping: 32 },
  commitThreshold: 0.32,     // fraction of card width to commit a level change
  commitVelocity: 460,       // px/s flick shortcut
  deeper:     { exitScale: 0.50, exitZ: -520, enterScale: 0.40, enterZ: -480 },
  shallower:  { exitScale: 1.55, exitZ: 400,  enterScale: 1.48, enterZ: 360 },
  tilt:       { x: 5, y: 9 }, // degrees during transition (felt, not seen)
  dragTilt:   7,
  dragScale:  0.07,
  nudge:      { firstDelay: 3000, repeatEvery: 12000, amount: 0.13 },
  totem:      { spins: 2, duration: 0.85 },
  // Level 1 brightest → 4/5 deepest richest gold → 6 surfaces again
  tints: [
    { bg: '#151107', border: 'rgba(212,175,55,0.40)', glow: '0 0 60px -12px rgba(212,175,55,0.55)' },
    { bg: '#17120A', border: 'rgba(212,175,55,0.34)', glow: '0 0 60px -14px rgba(212,175,55,0.45)' },
    { bg: '#1A1207', border: 'rgba(198,158,42,0.32)', glow: '0 0 66px -14px rgba(198,158,42,0.50)' },
    { bg: '#1D1204', border: 'rgba(184,138,20,0.46)', glow: '0 0 78px -10px rgba(184,138,20,0.62)' },
    { bg: '#1F1002', border: 'rgba(168,120,12,0.50)', glow: '0 0 84px -8px rgba(168,120,12,0.70)' },
    { bg: '#16120A', border: 'rgba(212,175,55,0.42)', glow: '0 0 62px -12px rgba(212,175,55,0.55)' },
  ],
};

const LEVELS = [
  { n: 1, name: 'Surface' },
  { n: 2, name: 'Beneath' },
  { n: 3, name: 'Deeper' },
  { n: 4, name: 'Core' },
  { n: 5, name: 'The Vault' },
  { n: 6, name: 'Surface' },
];

const FALLBACK_TICKERS = [
  { symbol: 'NVDA', price: 194.83, change_percent: '2.42' },
  { symbol: 'BTC-USD', price: 96421, change_percent: '3.11' },
  { symbol: 'MSFT', price: 390.49, change_percent: '-1.05' },
];

const fmtPrice = (p) => (p >= 1000 ? `$${Math.round(p).toLocaleString()}` : `$${p.toFixed(2)}`);

/* ------------------------------ Level content ----------------------------- */

const LevelHead = ({ label, tag, tone = 'text-gold' }) => (
  <div className="flex items-center justify-between mb-3">
    <p className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
    <span className={`text-[9px] uppercase tracking-widest font-bold ${tone}`}>{tag}</span>
  </div>
);

const SurfaceLevel = ({ tickers }) => (
  <div data-testid="dive-level-1">
    <LevelHead label="Level 1 · Surface" tag="Live" />
    <div className="space-y-2">
      {tickers.map((t) => {
        const pct = parseFloat(t.change_percent) || 0;
        const up = pct >= 0;
        return (
          <div key={t.symbol} className="flex items-center justify-between rounded-xl bg-white/[0.04] border border-white/[0.06] px-3 py-2">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center text-black font-black text-[9px]">
                {t.symbol[0]}
              </span>
              <div>
                <p className="text-white text-[11px] font-semibold leading-tight">{t.symbol}</p>
                <p className="text-zinc-500 text-[9px] font-mono">{fmtPrice(t.price)}</p>
              </div>
            </div>
            <span className={`font-mono text-[11px] font-bold flex items-center gap-1 ${up ? 'text-emerald-400' : 'text-red-400'}`}>
              {up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
              {up ? '+' : ''}{pct.toFixed(2)}%
            </span>
          </div>
        );
      })}
    </div>
    <p className="mt-3 text-[9px] text-zinc-600">Real quotes. Zero real money.</p>
  </div>
);

const PortfolioLevel = () => (
  <div data-testid="dive-level-2">
    <LevelHead label="Level 2 · Beneath" tag="Portfolio" tone="text-emerald-400" />
    <div className="flex items-end justify-between mb-1">
      <p className="font-unbounded font-black text-2xl text-white">+27.4%</p>
      <p className="text-[10px] text-emerald-400 font-mono">$9,271 unrealized</p>
    </div>
    <div className="rounded-xl bg-gradient-to-b from-emerald-500/12 to-transparent border border-emerald-500/10 p-2 mt-2">
      <svg viewBox="0 0 200 56" className="w-full h-16" preserveAspectRatio="none">
        <defs>
          <linearGradient id="dive-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,44 L25,40 L50,33 L75,37 L100,26 L125,20 L150,22 L175,12 L200,6 L200,56 L0,56 Z" fill="url(#dive-fill)" />
        <motion.path
          d="M0,44 L25,40 L50,33 L75,37 L100,26 L125,20 L150,22 L175,12 L200,6"
          fill="none" stroke="#10B981" strokeWidth="1.6"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: 'easeOut' }}
        />
      </svg>
    </div>
    <div className="flex items-center justify-between mt-2 text-[9px] text-zinc-500">
      <span>7-day growth</span><span>Simulated account</span>
    </div>
  </div>
);

const RankLevel = () => (
  <div data-testid="dive-level-3">
    <LevelHead label="Level 3 · Deeper" tag="Standing" />
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-700 flex flex-col items-center justify-center text-black shadow-[0_8px_24px_-8px_rgba(212,175,55,0.8)]">
        <Trophy size={18} weight="fill" />
        <span className="text-[9px] font-black">#12</span>
      </div>
      <div>
        <p className="font-unbounded font-bold text-white text-base leading-tight">#12 Global</p>
        <p className="text-[10px] text-gold flex items-center gap-1"><Crown size={11} weight="fill" /> Trader tier · 240 XP</p>
      </div>
    </div>
    <div className="mt-4">
      <div className="flex justify-between text-[9px] text-zinc-500 mb-1"><span>Trader</span><span>Investor · 300 XP</span></div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-yellow-300 to-yellow-600"
          initial={{ width: 0 }} animate={{ width: '68%' }} transition={{ duration: 0.8, ease: 'easeOut' }} />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1.5 mt-3">
      {['🎯 First Trade', '📈 Trader', '💥 Chaos'].map((a) => (
        <span key={a} className="text-[8px] text-zinc-300 bg-white/[0.05] border border-white/[0.06] rounded-lg px-1.5 py-1 text-center truncate">{a}</span>
      ))}
    </div>
  </div>
);

const AnalystLevel = ({ totemKey, reduced }) => (
  <div data-testid="dive-level-4" className="relative">
    <LevelHead label="Level 4 · Core" tag="AI Analyst" tone="text-purple-300" />
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-purple-500/15 border border-purple-400/20 flex items-center justify-center flex-shrink-0">
        <Robot size={18} weight="fill" className="text-purple-300" />
      </div>
      <div className="min-w-0">
        <p className="text-white font-unbounded font-bold text-sm leading-tight">NVDA · Balanced Buy</p>
        <p className="text-[10px] text-zinc-400 leading-snug mt-1">
          "Your portfolio leans hard into AI. Adding here raises concentration — size it small."
        </p>
      </div>
    </div>
    <div className="mt-3 space-y-1.5">
      {[['Conviction', 72, 'from-purple-400 to-fuchsia-500'], ['Risk fit', 48, 'from-yellow-300 to-yellow-600']].map(([label, v, grad]) => (
        <div key={label}>
          <div className="flex justify-between text-[9px] text-zinc-500"><span>{label}</span><span className="font-mono">{v}%</span></div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div className={`h-full bg-gradient-to-r ${grad}`}
              initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.7, delay: 0.1 }} />
          </div>
        </div>
      ))}
    </div>

    {/* THE TOTEM — spins once on arrival, non-blocking */}
    <motion.div
      key={totemKey}
      className="absolute -bottom-3 right-1 pointer-events-none"
      style={{ transformStyle: reduced ? 'flat' : 'preserve-3d' }}
      initial={{ scale: 0.4, opacity: 0, rotateY: 0 }}
      animate={{
        scale: [0.4, 1.18, 0.94, 1],
        opacity: [0, 1, 1, 1],
        rotateY: reduced ? 0 : DIVE_CONFIG.totem.spins * 360,
      }}
      transition={{ duration: DIVE_CONFIG.totem.duration, ease: [0.22, 1.2, 0.36, 1] }}
      data-testid="dive-totem"
    >
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-400 to-yellow-700 flex items-center justify-center shadow-[0_6px_22px_-4px_rgba(212,175,55,0.9)] border border-yellow-200/60">
        <ChartLineUp size={16} weight="bold" className="text-black" />
      </div>
    </motion.div>
  </div>
);

const VaultLevel = () => (
  <div data-testid="dive-level-5">
    <LevelHead label="Level 5 · The Vault" tag="Bets" tone="text-cyan-300" />
    <div className="flex items-center justify-between gap-2">
      {[{ n: 'You', c: 'from-yellow-300 to-yellow-600', s: 'Above $200' },
        { n: 'Aarav', c: 'from-cyan-300 to-blue-600', s: 'Below $200' }].map((p, i) => (
        <React.Fragment key={p.n}>
          {i === 1 && (
            <motion.span
              className="text-[10px] font-unbounded font-black text-zinc-500 px-1"
              animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity }}
            >VS</motion.span>
          )}
          <div className="flex-1 rounded-xl bg-white/[0.04] border border-white/[0.07] p-2.5 text-center">
            <div className={`w-9 h-9 mx-auto rounded-full bg-gradient-to-br ${p.c} flex items-center justify-center text-black font-black text-xs`}>
              {p.n[0]}
            </div>
            <p className="text-white text-[10px] font-semibold mt-1.5">{p.n}</p>
            <p className="text-[9px] text-zinc-500">{p.s}</p>
          </div>
        </React.Fragment>
      ))}
    </div>
    <div className="mt-3 rounded-xl bg-gold/[0.07] border border-gold/20 px-3 py-2 flex items-center justify-between">
      <div>
        <p className="text-[9px] uppercase tracking-widest text-zinc-500">NVDA by Friday</p>
        <p className="text-white text-[11px] font-mono font-bold">$194.83 <span className="text-zinc-500">→ $200.00</span></p>
      </div>
      <Coins size={20} weight="fill" className="text-gold" />
    </div>
    <p className="mt-2 text-[9px] text-zinc-600">Peer predictions. Bragging rights only.</p>
  </div>
);

const CtaLevel = ({ onCta }) => (
  <div data-testid="dive-level-6" className="h-full flex flex-col justify-center">
    <LevelHead label="Level 6 · Surface" tag="Ready" />
    <p className="font-unbounded font-black text-white text-lg leading-tight">
      You just dived through<br />
      <span className="bg-gradient-to-r from-yellow-200 to-yellow-600 bg-clip-text text-transparent">the whole app.</span>
    </p>
    <p className="text-[11px] text-zinc-400 mt-2">$25,000 virtual cash waiting. No card, no risk.</p>
    <button
      onClick={onCta}
      className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 text-black font-bold text-[11px] px-4 py-2.5 hover:scale-[1.03] transition-transform"
      data-testid="dive-cta-btn"
    >
      <DeviceMobile size={14} weight="fill" /> Install TeenVest free
    </button>
  </div>
);

/* ------------------------------ Depth gauge ------------------------------- */

const GAUGE_H = 148;

const DepthGauge = ({ level, depth }) => {
  const markerY = useTransform(depth, (v) => {
    const clamped = Math.max(0, Math.min(LEVELS.length - 1, v));
    return (clamped / (LEVELS.length - 1)) * (GAUGE_H - 10);
  });

  return (
    <div className="relative flex flex-col items-center select-none w-6 xl:w-10 pl-1" style={{ height: GAUGE_H }} data-testid="dive-depth-gauge">
      <div className="absolute top-0 bottom-0 left-[3px] w-[2px] bg-gradient-to-b from-gold/50 via-gold/20 to-yellow-900/40 rounded-full" />
      <motion.div
        className="absolute left-0 w-2.5 h-2.5 rounded-full bg-gold shadow-[0_0_12px_2px_rgba(212,175,55,0.8)] z-10"
        style={{ y: markerY }}
        data-testid="dive-gauge-marker"
      />
      <div className="flex flex-col justify-between h-full w-full">
        {LEVELS.map((l, i) => (
          <div key={l.n} className="flex items-center gap-1.5 h-2.5">
            <span className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i <= level ? 'bg-gold/80' : 'bg-white/15'}`} />
            <span className={`hidden xl:block text-[7px] uppercase tracking-widest transition-colors duration-300 ${i === level ? 'text-gold' : 'text-zinc-600'}`}>
              L{l.n}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------ Main component ---------------------------- */

const DiveDeeperStack = ({ onCta }) => {
  const [level, setLevel] = useState(0);
  const [dir, setDir] = useState(1);
  const [tickers, setTickers] = useState(FALLBACK_TICKERS);
  const [totemKey, setTotemKey] = useState(0);
  const [width, setWidth] = useState(360);
  const interacted = useRef(false);
  const nudgeAnims = useRef([]);
  const boxRef = useRef(null);
  const x = useMotionValue(0);
  const nudgeX = useMotionValue(0);   // discovery hint only — kept off the drag axis

  const reduced = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const rm = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const cores = navigator.hardwareConcurrency || 8;
    const mem = navigator.deviceMemory || 8;
    return Boolean(rm) || cores <= 2 || mem <= 2;
  }, []);

  const cfg = DIVE_CONFIG;

  // Real quotes for Level 1 (public endpoint, silent static fallback)
  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/hero-quotes`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.quotes?.length) setTickers(d.quotes); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Measure card width for drag thresholds
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth || 360);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const nudgeScale = useTransform(nudgeX, [0, width * cfg.nudge.amount], [1, 0.97]);
  const depth = useTransform([x, nudgeX], ([dragX, hintX]) => level + (dragX + hintX * 0.5) / width);
  const dragRotateY = useTransform(x, [-width, 0, width], reduced ? [0, 0, 0] : [cfg.dragTilt, 0, -cfg.dragTilt]);
  const dragScale = useTransform(x, [-width, 0, width], [1 + cfg.dragScale, 1, 1 - cfg.dragScale]);

  const go = useCallback((d) => {
    const next = level + d;
    if (next < 0 || next >= LEVELS.length) {
      animate(x, 0, cfg.dragSpring);
      return;
    }
    setDir(d);
    setLevel(next);
    animate(x, 0, cfg.dragSpring);
  }, [level, x, cfg.dragSpring]);

  // The hint runs on its own motion value, so a drag can never be swallowed by it.
  const markInteracted = () => {
    interacted.current = true;
    nudgeAnims.current.forEach((c) => c?.stop?.());
    nudgeAnims.current = [];
    animate(nudgeX, 0, { type: 'spring', stiffness: 300, damping: 30 });
  };

  // Discovery hint: partial dive at 3s, repeating every 12s until first touch
  useEffect(() => {
    let cancelled = false;
    const nudge = () => {
      if (cancelled || interacted.current) return;
      nudgeAnims.current.push(animate(nudgeX, width * cfg.nudge.amount, { type: 'spring', stiffness: 160, damping: 16 }));
      setTimeout(() => {
        if (!cancelled && !interacted.current) {
          nudgeAnims.current.push(animate(nudgeX, 0, { type: 'spring', stiffness: 140, damping: 18 }));
        }
      }, 420);
    };
    const t = setTimeout(nudge, cfg.nudge.firstDelay);
    const iv = setInterval(nudge, cfg.nudge.repeatEvery);
    return () => { cancelled = true; clearTimeout(t); clearInterval(iv); };
  }, [width, nudgeX, cfg.nudge]);

  // Totem flourish on arriving at Level 4
  useEffect(() => { if (level === 3) setTotemKey((k) => k + 1); }, [level]);

  const handleDragEnd = (_e, info) => {
    const commit =
      Math.abs(info.offset.x) > width * cfg.commitThreshold ||
      Math.abs(info.velocity.x) > cfg.commitVelocity;
    if (commit) go(info.offset.x > 0 ? 1 : -1);
    else animate(x, 0, cfg.dragSpring);
  };

  const variants = {
    enter: (d) => {
      const s = d > 0 ? cfg.deeper : cfg.shallower;
      return reduced
        ? { opacity: 0, scale: d > 0 ? 0.85 : 1.12 }
        : { opacity: 0, scale: s.enterScale, z: s.enterZ, rotateX: d > 0 ? cfg.tilt.x : -cfg.tilt.x, rotateY: d > 0 ? -cfg.tilt.y : cfg.tilt.y };
    },
    center: reduced
      ? { opacity: 1, scale: 1 }
      : { opacity: 1, scale: 1, z: 0, rotateX: 0, rotateY: 0 },
    exit: (d) => {
      const s = d > 0 ? cfg.deeper : cfg.shallower;
      return reduced
        ? { opacity: 0, scale: d > 0 ? 0.85 : 1.12 }
        : { opacity: 0, scale: s.exitScale, z: s.exitZ, rotateX: d > 0 ? -cfg.tilt.x : cfg.tilt.x, rotateY: d > 0 ? cfg.tilt.y : -cfg.tilt.y };
    },
  };

  const tint = cfg.tints[level];

  const renderLevel = (i) => {
    if (i === 0) return <SurfaceLevel tickers={tickers} />;
    if (i === 1) return <PortfolioLevel />;
    if (i === 2) return <RankLevel />;
    if (i === 3) return <AnalystLevel totemKey={totemKey} reduced={reduced} />;
    if (i === 4) return <VaultLevel />;
    return <CtaLevel onCta={onCta} />;
  };

  return (
    <div className="flex items-center gap-3" data-testid="dive-deeper-stack">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2 px-0.5">
          <p className="text-[9px] uppercase tracking-[0.3em] text-gold flex items-center gap-1.5">
            <Lightning size={10} weight="fill" /> Dive Deeper
          </p>
          <p className="text-[9px] text-zinc-500">{LEVELS[level].name} · {level + 1}/6</p>
        </div>

        <div
          ref={boxRef}
          className="relative h-[236px] sm:h-[252px]"
          style={{ perspective: reduced ? undefined : cfg.perspective }}
        >
          {/* Persistent depth-graded backdrop (colour grading by depth) */}
          <motion.div
            className="absolute inset-0 rounded-2xl border"
            animate={{ backgroundColor: tint.bg, borderColor: tint.border, boxShadow: tint.glow }}
            transition={cfg.spring}
            data-testid="dive-backdrop"
          />

          <motion.div
            className="absolute inset-0"
            style={{ x: nudgeX, scale: nudgeScale, transformStyle: reduced ? 'flat' : 'preserve-3d' }}
          >
            <AnimatePresence initial={false} custom={dir}>
              <motion.div
                key={level}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={cfg.spring}
                className="absolute inset-0"
                style={{ transformStyle: reduced ? 'flat' : 'preserve-3d' }}
              >
                <motion.div
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.5}
                  dragMomentum={false}
                  onDragStart={markInteracted}
                  onDragEnd={handleDragEnd}
                  style={{ x, rotateY: dragRotateY, scale: dragScale }}
                  className="absolute inset-0 rounded-2xl bg-black/45 backdrop-blur-xl border border-white/[0.07] p-4 cursor-grab active:cursor-grabbing overflow-hidden"
                  data-testid={`dive-card-${level + 1}`}
                >
                  <div className="h-full flex flex-col justify-center pb-7">
                    {renderLevel(level)}
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Non-gesture fallback */}
          <button
            onClick={() => { markInteracted(); go(-1); }}
            disabled={level === 0}
            className="absolute left-2 bottom-2 z-20 w-7 h-7 rounded-full bg-black/70 border border-white/10 text-zinc-300 flex items-center justify-center disabled:opacity-25 hover:text-gold hover:border-gold/40 transition-colors"
            aria-label="Surface up one level"
            data-testid="dive-prev-btn"
          >
            <CaretLeft size={13} weight="bold" />
          </button>
          <button
            onClick={() => { markInteracted(); go(1); }}
            disabled={level === LEVELS.length - 1}
            className="absolute right-2 bottom-2 z-20 w-7 h-7 rounded-full bg-black/70 border border-gold/30 text-gold flex items-center justify-center disabled:opacity-25 hover:border-gold/70 transition-colors"
            aria-label="Dive one level deeper"
            data-testid="dive-next-btn"
          >
            <CaretRight size={13} weight="bold" />
          </button>
        </div>

        <p className="mt-2 text-[9px] text-zinc-600 text-center">Drag right to dive deeper · left to surface</p>
      </div>

      <DepthGauge level={level} depth={depth} />
    </div>
  );
};

export default DiveDeeperStack;
